import { Sun, Wind, Globe, ChevronRight, Award, Users, Zap, ArrowRight, Lock, MapPin, Calendar, Activity } from "lucide-react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";
import { useState, useRef, useEffect } from "react";
import { BackgroundImage } from "../../components/ui/ImageComponent";
import { IMAGE_SETS } from "../../constants/images";

const LandingPage = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleCanPlay = () => setIsVideoPlaying(true);
      const handleError = () => setVideoError(true);
      
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', handleError);
      
      return () => {
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
      };
    }
  }, []);

  return (
    <section className="relative w-full min-h-screen overflow-hidden" style={{ background: 'linear-gradient(to bottom, transparent 0%, transparent 50%, white 50%, white 100%)' }}>
      {/* Background Container - Limited to 50% height */}
      <div 
        className="absolute top-0 left-0 w-full overflow-hidden z-0 background-container-50"
        style={{ height: '50vh', maxHeight: '50vh' }}
      >
        {/* Video background with overlay */}
        <video
          ref={videoRef}
          className="absolute top-0 left-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src="/assets/energy.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          Your browser does not support the video tag.
        </video>
        
        {/* Loading state for video */}
        {!isVideoPlaying && !videoError && (
          <div className="absolute inset-0 video-loading flex items-center justify-center">
            <div className="text-white text-lg font-medium">Loading...</div>
          </div>
        )}
        
        {/* Fallback background if video fails to load */}
        {videoError && (
          <BackgroundImage
            src={IMAGE_SETS.hero.energy}
            className="absolute inset-0"
            overlay={false}
          />
        )}
        
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>
      </div>

      {/* White background overlay for bottom half */}
      <div 
        className="absolute bottom-0 left-0 w-full bg-white z-1"
        style={{ height: '50vh' }}
      ></div>

      {/* Authorized Access Notice - Eye-catching position */}


      {/* Hero Section - Mobile First Responsive */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full max-w-8xl mx-auto px-2 lg:px-4 xl:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left Column - Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              className="text-white space-y-6 lg:space-y-8"
            >
              {/* Main Headline - Strong Value Proposition */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4 }}
              >
                <h1 className="text-xl sm:text-2xl lg:text-2xl xl:text-5xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-red-600 via-red-200 to-[#fb7185] bg-clip-text text-transparent">
                    AxonMetric
                  </span>
                  <br />
                  <span className="text-white">For the energy leaders</span>
                </h1>
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mt-4 max-w-2xl">
                  AxonMetric Global Private Limited (Axon) is a cutting-edge  
                  <span className="text-red-200 font-semibold"> technology company in Power domain</span> committed to improve performance of renewable assets.
                </p>
              </motion.div>

              {/* Single Primary CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <a
                  href="#site-select"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-[#0084CF] hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-4 px-8 rounded-xl shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105"
                >
                  <span>Explore</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </motion.div>
            </motion.div>

            {/* Right Column - Bigger Interactive Stats Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="relative lg:max-w-xl ml-auto"
            >
              <div className="backdrop-blur-2xl bg-gradient-to-br from-white/15 to-white/5 border border-white/30 shadow-2xl rounded-3xl p-8 space-y-8 mt-16 h-64 w-full flex flex-col">
                
                {/* Header with Status */}
                <div className="text-center flex-shrink-0">    
                  <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-[#0084CF] bg-clip-text text-transparent mb-2">
                    <CountUp end={3.0} duration={3} decimals={1} />+ GW
                  </h2>
                  <p className="text-sm text-white/90 font-medium">Total Energy Production</p>
                </div>

                {/* Plant Types - Enhanced Grid */}
                <div className="grid grid-cols-2 gap-4 flex-1">
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    className="group p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-yellow-400/50 cursor-pointer transition-all duration-300"
                  >
                    <Sun className="text-yellow-400 mb-2 group-hover:rotate-12 transition-transform duration-300" size={20} />
                    <div className="text-xl font-bold text-white mb-1">
                      <CountUp end={11} duration={2.5} />
                    </div>
                    <div className="text-white/80 text-xs font-medium mb-1">Solar Plants</div>
                    <div className="text-yellow-400 text-xs">2.6+ MW</div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    className="group p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-blue-400/50 cursor-pointer transition-all duration-300"
                  >
                    <Wind className="text-blue-400 mb-2 group-hover:rotate-12 transition-transform duration-300" size={20} />
                    <div className="text-xl font-bold text-white mb-1">
                      <CountUp end={2} duration={2.5} />
                    </div>
                    <div className="text-white/80 text-xs font-medium mb-1">Wind Farms</div>
                    <div className="text-blue-400 text-xs">370+ GW</div>
                  </motion.div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-white/20 flex-shrink-0">
                  <div className="text-center">
                    <div className="text-lg font-bold text-cyan-400 mb-1">
                      <CountUp end={13} duration={2.5} />
                    </div>
                    <div className="text-xs text-black font-medium">Total Sites</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-400 mb-1">24/7</div>
                    <div className="text-xs text-black font-medium">Monitoring</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-400 mb-1">2017</div>
                    <div className="text-xs text-white/70 font-medium">Since</div>
                  </div>
                </div>

                {/* Location Info */}
                <div className="bg-white/5 rounded-xl p-2 border border-white/10 flex-shrink-0">
                  <div className="flex items-center gap-2 text-white/80 text-xs">
                    <MapPin size={12} className="text-[#0084CF]" />
                    <span className="font-medium text-black">Key Offerings</span>
                  </div>
                  <p className="text-black text-xs mt-1">Performance, Forecasting, RTC Optimization, Trading-Optimizer</p>
                </div>
              </div>


            </motion.div>


           
          </div>
        </div>
      </div>

      {/* Floating Action Buttons for Mobile */}
      <div className="fixed bottom-6 right-6 z-30 lg:hidden">
        <motion.a
          href="#site-select"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 2 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="flex items-center justify-center w-14 h-14 bg-gradient-to-r from-cyan-500 to-[#0084CF] text-white rounded-full shadow-2xl shadow-cyan-500/25"
        >
          <Zap size={24} />
        </motion.a>
      </div>
    </section>
  );
};

export default LandingPage;
